import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Stack, Tooltip } from '@mui/material';
import { Resolver, useForm, useFormState, useWatch } from 'react-hook-form';

import { updateInfo } from '@/api/user';

import { Select } from '@/components/Form/Select/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon';

import { getAssetPath } from '@/constants/configs';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectDefaultCurrency } from '@/redux/configs/baseSelectors';

import { Option } from '@/types/components/common';
import { IncomeSource, PayFrequency } from '@/types/user';

import { FinanceQuestion } from '../../hooks';
import {
  CharactersLeft,
  CurrencyLabel,
  FieldLabel,
  FinanceBlock,
  FinancesQuestionForm,
  InfoHint,
} from '../common';
import {
  getIncomeAndSpendingValidationSchema,
  MAX_INCOME_DIGITS,
  MAX_INCOME_SOURCES,
  MAX_OTHER_FREQUENCY_LENGTH,
  MAX_OTHER_SOURCE_LENGTH,
  MAX_SPENDING_DIGITS,
} from './validationSchema';

type FormValues = {
  totalMonthlyIncome: string;
  totalMonthlySpending: string;
  sourceOfIncome: IncomeSource[];
  sourceOfIncomeOther: string;
  howOftenPaid: PayFrequency;
  howOftenPaidOther: string;
};

type QuestionIncomeAndSpendingProps = {
  onSubmit: () => void;
};

export function QuestionIncomeAndSpending({
  onSubmit,
}: Readonly<QuestionIncomeAndSpendingProps>) {
  const { t } = useTranslate();
  const { currencyDisplay } = useAppSelector(selectDefaultCurrency);

  const { mutate, isPending } = useMutationWithUserData({
    mutationFn: updateInfo,
    onFinished: () => {
      onSubmit();
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: {
      totalMonthlyIncome: '',
      sourceOfIncome: [],
      sourceOfIncomeOther: '',
      howOftenPaid: PayFrequency.Monthly,
      howOftenPaidOther: '',
      totalMonthlySpending: '',
    },
    mode: 'onChange',
    resolver: yupResolver(
      getIncomeAndSpendingValidationSchema(t),
    ) as Resolver<FormValues>,
  });

  const { errors } = useFormState({ control });

  const sourceOfIncome = useWatch({ control, name: 'sourceOfIncome' });
  const howOftenPaid = useWatch({ control, name: 'howOftenPaid' });
  const sourceOfIncomeOtherValue =
    useWatch({ control, name: 'sourceOfIncomeOther' }) ?? '';
  const howOftenPaidOtherValue =
    useWatch({ control, name: 'howOftenPaidOther' }) ?? '';

  const showOtherIncomeSource = sourceOfIncome.includes(IncomeSource.Other);
  const showOtherPayFrequency = howOftenPaid === PayFrequency.Other;

  const incomeSourceOptions: Option[] = Object.values(IncomeSource).map(
    source => ({
      value: source,
      text: t(`moreInfo.form.yourFinances.income.source.${source}`),
    }),
  );

  const payFrequencyOptions: Option[] = Object.values(PayFrequency).map(
    freq => ({
      value: freq,
      text: t(`moreInfo.form.yourFinances.income.frequency.${freq}`),
    }),
  );

  const handleIncomeSourceChange = (selectedValues: unknown) => {
    if (!Array.isArray(selectedValues)) return;

    const newValues = selectedValues as IncomeSource[];
    const isOtherSelected =
      newValues.includes(IncomeSource.Other) &&
      !sourceOfIncome.includes(IncomeSource.Other);

    // "Other" is mutually exclusive with other options
    const resolvedSources = isOtherSelected
      ? [IncomeSource.Other]
      : newValues.filter(v => v !== IncomeSource.Other);

    setValue('sourceOfIncome', resolvedSources, { shouldValidate: true });

    if (!isOtherSelected) {
      setValue('sourceOfIncomeOther', '', { shouldValidate: true });
    }
  };

  const onContinue = handleSubmit(formData => {
    mutate({
      ...formData,
      sourceOfIncome: JSON.stringify(formData.sourceOfIncome),
      totalMonthlyIncome: Number(formData.totalMonthlyIncome),
      totalMonthlySpending: Number(formData.totalMonthlySpending),
    });
  });

  return (
    <FinancesQuestionForm
      question={FinanceQuestion.IncomeAndSpending}
      title={t('moreInfo.form.yourFinances.income.title')}
      imageUrl={getAssetPath('assets/images/icon-income-and-spending.svg')}
      onContinue={onContinue}
      disable={!isValid}
      isLoading={isPending}
    >
      {/* Total Monthly Income */}
      <FinanceBlock
        title={t('moreInfo.form.yourFinances.income.monthlyIncomeLabel')}
      >
        <FieldLabel>
          {t('moreInfo.form.yourFinances.income.monthlyIncomeQuestion')}
        </FieldLabel>
        <TextInput<FormValues>
          control={control}
          name="totalMonthlyIncome"
          error={!!errors.totalMonthlyIncome}
          helperText={errors.totalMonthlyIncome?.message}
          placeholder={t(
            'moreInfo.form.yourFinances.income.monthlyIncomePlaceholder',
          )}
          inputMode="numeric"
          fullWidth
          size="small"
          template="currency"
          startAdornment={<CurrencyLabel>{currencyDisplay}</CurrencyLabel>}
          inputProps={{ maxLength: MAX_INCOME_DIGITS }}
        />
        <InfoHint
          text={t('moreInfo.form.yourFinances.income.monthlyIncomeTooltip')}
        />
      </FinanceBlock>

      {/* Income Sources */}
      <FinanceBlock
        title={t('moreInfo.form.yourFinances.income.incomeSourcesLabel')}
      >
        <Box mb={2}>
          <FieldLabel>
            {t('moreInfo.form.yourFinances.income.incomeSourcesQuestion')}
          </FieldLabel>
          <Select<FormValues>
            control={control}
            name="sourceOfIncome"
            options={incomeSourceOptions}
            multiple
            maxSelections={MAX_INCOME_SOURCES}
            placeholder={t(
              'moreInfo.form.yourFinances.income.incomeSourcesPlaceholder',
            )}
            error={!!errors.sourceOfIncome}
            helperText={errors.sourceOfIncome?.message}
            fullWidth
            size="small"
            onSelectChange={e => handleIncomeSourceChange(e.target.value)}
            sx={{ backgroundColor: 'common.white' }}
          />
        </Box>

        {/* Conditional: Other income source */}
        {showOtherIncomeSource && (
          <Box mb={2}>
            <FieldLabel>
              {t('moreInfo.form.yourFinances.income.otherIncomeSourceLabel')}
            </FieldLabel>
            <TextInput<FormValues>
              control={control}
              name="sourceOfIncomeOther"
              error={!!errors.sourceOfIncomeOther}
              helperText={errors.sourceOfIncomeOther?.message}
              placeholder={t(
                'moreInfo.form.yourFinances.income.otherIncomeSourcePlaceholder',
              )}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_OTHER_SOURCE_LENGTH }}
            />
            <CharactersLeft
              count={MAX_OTHER_SOURCE_LENGTH - sourceOfIncomeOtherValue.length}
            />
          </Box>
        )}

        {/* Pay Frequency */}
        <Box mb={2}>
          <FieldLabel>
            {t('moreInfo.form.yourFinances.income.payFrequencyLabel')}
          </FieldLabel>
          <Stack direction="row" alignItems="flex-start" gap={1}>
            <Select<FormValues>
              control={control}
              name="howOftenPaid"
              options={payFrequencyOptions}
              placeholder={t(
                'moreInfo.form.yourFinances.income.payFrequencyPlaceholder',
              )}
              helperText={errors.howOftenPaid?.message}
              error={!!errors.howOftenPaid}
              fullWidth
              size="small"
              sx={{ backgroundColor: 'common.white' }}
            />
            <Tooltip
              title={t('moreInfo.form.yourFinances.income.payFrequencyTooltip')}
              arrow
              enterTouchDelay={0}
            >
              <Stack
                justifyContent="center"
                sx={{ height: 40, cursor: 'pointer' }}
              >
                <Icon
                  name="info-outline"
                  size={24}
                  color={theme => theme.palette.neutral[700]}
                />
              </Stack>
            </Tooltip>
          </Stack>
        </Box>

        {/* Conditional: Other pay frequency */}
        {showOtherPayFrequency && (
          <Box>
            <FieldLabel>
              {t('moreInfo.form.yourFinances.income.otherPayFrequencyLabel')}
            </FieldLabel>
            <TextInput<FormValues>
              control={control}
              name="howOftenPaidOther"
              error={!!errors.howOftenPaidOther}
              helperText={errors.howOftenPaidOther?.message}
              placeholder={t(
                'moreInfo.form.yourFinances.income.otherPayFrequencyPlaceholder',
              )}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_OTHER_FREQUENCY_LENGTH }}
            />
            <CharactersLeft
              count={MAX_OTHER_FREQUENCY_LENGTH - howOftenPaidOtherValue.length}
            />
          </Box>
        )}
      </FinanceBlock>

      {/* Total Monthly Spending */}
      <FinanceBlock
        title={t('moreInfo.form.yourFinances.income.monthlySpendingLabel')}
      >
        <TextInput<FormValues>
          control={control}
          name="totalMonthlySpending"
          error={!!errors.totalMonthlySpending}
          helperText={errors.totalMonthlySpending?.message}
          placeholder={t(
            'moreInfo.form.yourFinances.income.monthlySpendingPlaceholder',
          )}
          inputMode="numeric"
          fullWidth
          size="small"
          template="currency"
          startAdornment={<CurrencyLabel>{currencyDisplay}</CurrencyLabel>}
          inputProps={{ maxLength: MAX_SPENDING_DIGITS }}
        />
        <InfoHint
          text={t('moreInfo.form.yourFinances.income.monthlySpendingTooltip')}
        />
      </FinanceBlock>
    </FinancesQuestionForm>
  );
}
